import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch, useController } from 'react-hook-form'
import { toast } from 'sonner'
import { FormTextarea, FormSelect, FormSwitch, FormMdxEditor } from '@/components/forms'
import { LoadingSpinner } from '@/components/common'
import { Button as StyledButton } from '@/components/base/buttons'
import { useAIDeployments, usePermissions, type AIDeployment } from '@/hooks/api'
import { useActivateRedirect } from '@/hooks/api/useWhatsAppSignup'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/style.css'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'
import {
  Select as AriaSelect,
  SelectValue,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
} from 'react-aria-components'
import { ChevronDownIcon } from '@untitledui-icons/react/line'

/**
 * Section Header component for consistent styling across the tab
 */
interface SectionHeaderProps {
  title: string
  description?: string
}

function SectionHeader({ title, description }: SectionHeaderProps) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {title}
      </h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
    </div>
  )
}

/**
 * Custom SelectItem for deployment options showing vendor chip
 */
function DeploymentSelectItem({
  vendor,
  label,
  ...props
}: ListBoxItemProps & { vendor?: string; label: string }) {
  return (
    <ListBoxItem
      {...props}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer outline-none',
        'text-gray-900 dark:text-gray-100',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        'focus:bg-gray-100 dark:focus:bg-gray-800',
        'selected:bg-brand-50 selected:text-brand-700 dark:selected:bg-brand-950 dark:selected:text-brand-300'
      )}
    >
      {vendor && (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          {vendor}
        </span>
      )}
      <span>{label}</span>
    </ListBoxItem>
  )
}

/**
 * DeploymentSelector component - fetches AI models from backend
 * Shows vendor chip next to each model name (like v1)
 */
function DeploymentSelector() {
  const { t } = useTranslation()
  const { control, watch } = useFormContext()
  const assistantId = watch('id')

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name: 'ai_deployment_id',
  })

  const {
    data: deployments = [],
    isLoading,
    error: fetchError,
  } = useAIDeployments(assistantId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3">
        <LoadingSpinner size="sm" />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.admin.loading_ai_models')}
        </span>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
        {t('resources.assistants.admin.failed_to_load_ai_models')}
      </div>
    )
  }

  // Find the selected deployment to show in the button
  const selectedDeployment = deployments.find(
    (d: AIDeployment) => d.id === value
  )

  return (
    <AriaSelect
      className="flex flex-col gap-1.5"
      selectedKey={value?.toString()}
      onSelectionChange={(key) => onChange(key ? Number(key) : null)}
      aria-label="Select AI model"
    >
      <Button
        className={cn(
          'flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-base',
          'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
          'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
          'dark:focus:border-brand-400 dark:focus:ring-brand-400',
          error && 'border-error-500 focus:border-error-500 focus:ring-error-500'
        )}
      >
        <SelectValue className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
          {selectedDeployment ? (
            <>
              {selectedDeployment.vendor && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {selectedDeployment.vendor}
                </span>
              )}
              <span>
                {selectedDeployment.friendly_name || selectedDeployment.model}
              </span>
            </>
          ) : (
            <span className="text-gray-400">{t('resources.assistants.admin.select_ai_model')}</span>
          )}
        </SelectValue>
        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
      </Button>
      <Popover
        className={cn(
          'w-[var(--trigger-width)] rounded-lg border border-gray-200 bg-white shadow-lg',
          'dark:bg-gray-900 dark:border-gray-700',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox className="p-1 max-h-60 overflow-auto outline-none">
          {deployments.map((deployment: AIDeployment) => (
            <DeploymentSelectItem
              key={deployment.id}
              id={deployment.id.toString()}
              textValue={deployment.friendly_name || deployment.model}
              vendor={deployment.vendor}
              label={deployment.friendly_name || deployment.model}
            />
          ))}
        </ListBox>
      </Popover>
      {error && (
        <p className="text-sm text-error-600 dark:text-error-400">
          {error.message}
        </p>
      )}
    </AriaSelect>
  )
}

/**
 * Custom JSON Editor component using react18-json-view
 * Allows direct editing of the config object
 */
function CustomJsonEditor() {
  const { setValue, trigger, control } = useFormContext()
  const config = useWatch({ control, name: 'config' }) || {}
  const { resolvedTheme } = useUIStore()
  const isDarkMode = resolvedTheme === 'dark'

  const handleChange = ({
    src,
  }: {
    src: Record<string, unknown>
    newValue: unknown
    oldValue: unknown
  }) => {
    // Update the config object
    setValue('config', src, {
      shouldDirty: true,
      shouldTouch: true,
    })

    // Trigger validation to update save button
    trigger('config')
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isDarkMode
          ? 'border-gray-700 bg-gray-800'
          : 'border-gray-200 bg-gray-50'
      )}
    >
      <JsonView
        src={config}
        theme={isDarkMode ? 'a11y' : 'github'}
        enableClipboard={true}
        displaySize={true}
        editable={{
          edit: true,
          add: true,
          delete: true,
        }}
        collapsed={false}
        style={{
          backgroundColor: 'transparent',
          padding: '4px',
          fontSize: '13px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
        onChange={handleChange}
      />
    </div>
  )
}

/**
 * Admin Settings Tab - Advanced configuration (admin/partner only)
 *
 * Includes:
 * - Deployment selector (AI model)
 * - Role/persona prompt
 * - Call policy (MDXEditor)
 * - Context (MDXEditor)
 * - Technical settings (human time format, interruption, STT backend)
 * - Custom JSON configuration
 */
function WhatsAppRedirectDebug() {
  const { watch } = useFormContext()
  const assistantId = watch('id') as number | undefined
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const activate = useActivateRedirect()

  const submit = async () => {
    if (!assistantId) return
    if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
      setError('Use E.164 format (e.g. +31612345678)')
      return
    }
    setError(null)
    try {
      await activate.mutateAsync({ assistant_id: assistantId, redirect_phone_number: phone })
      toast.success('Redirect activated — incoming Meta calls will forward here')
    } catch (e) {
      toast.error(`Activation failed: ${e instanceof Error ? e.message : 'unknown'}`)
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
      <SectionHeader
        title="WhatsApp verification-redirect (dev)"
        description="Forward Meta's verification call for this assistant's Bonnie number to a personal phone. Expires by TTL."
      />
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="+31612345678"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value)
            if (error) setError(null)
          }}
          className="w-56 rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
        <StyledButton
          type="button"
          color="secondary"
          size="sm"
          isLoading={activate.isPending}
          onPress={submit}
        >
          Activate redirect
        </StyledButton>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </section>
  )
}

export function AdminSettingsTab() {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const { isAdmin } = usePermissions()

  return (
    <div className="space-y-8">
      {/* Deployment Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <SectionHeader
          title={t('resources.assistants.admin.deployment')}
          description={t('resources.assistants.admin.deployment_description')}
        />
        <DeploymentSelector />
      </section>

      {/* Prompt Scenarios Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-8">
          {/* Role/Persona */}
          <div>
            <SectionHeader
              title={t('resources.assistants.admin.assistant_role')}
              description={t('resources.assistants.admin.assistant_role_description')}
            />
            <FormTextarea
              name="prompt_scenarios.role"
              control={control}
              placeholder={t('resources.assistants.admin.role_placeholder')}
              autoResize
            />
          </div>

          {/* Call Policy */}
          <div>
            <SectionHeader
              title={t('resources.assistants.admin.call_policy')}
              description={t('resources.assistants.admin.call_policy_description')}
            />
            <FormMdxEditor
              name="prompt_scenarios.call_policy"
              control={control}
              placeholder={t('resources.assistants.admin.call_policy_placeholder')}
              minHeight="200px"
            />
          </div>

          {/* Context */}
          <div>
            <SectionHeader
              title={t('resources.assistants.admin.context')}
              description={t('resources.assistants.admin.context_description')}
            />
            <FormMdxEditor
              name="prompt_scenarios.context"
              control={control}
              placeholder={t('resources.assistants.admin.context_placeholder')}
              minHeight="200px"
            />
          </div>
        </div>
      </section>

      {/* Technical Settings Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="mb-6 text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.admin.technical_settings')}
        </h2>

        <div className="space-y-6">
          {/* Human Time Format */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SectionHeader
                title={t('resources.assistants.admin.human_time_format')}
                description={t('resources.assistants.admin.human_time_format_description')}
              />
            </div>
            <div className="pt-1">
              <FormSwitch name="config.tts.use_human_time_format" control={control} />
            </div>
          </div>

          {/* Allow Interruption (admin only) */}
          {isAdmin && (
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <SectionHeader
                  title={t('resources.assistants.admin.allow_interruption')}
                  description={t('resources.assistants.admin.allow_interruption_description')}
                />
              </div>
              <div className="pt-1">
                <FormSwitch name="config.stt.interruption" control={control} />
              </div>
            </div>
          )}

          {/* Speech-to-Text Backend */}
          <div>
            <SectionHeader
              title={t('resources.assistants.admin.stt_backend')}
              description={t('resources.assistants.admin.stt_backend_description')}
            />
            <div className="max-w-xs">
              <FormSelect
                name="config.stt.backend"
                control={control}
                options={[
                  { value: 'azure', label: 'Microsoft Azure' },
                  { value: 'deepgram', label: 'Deepgram' },
                  { value: 'reson8', label: 'reson8' },
                ]}
                placeholder={t('resources.assistants.admin.select_stt_backend')}
              />
            </div>
          </div>

          {/* Always Run Post-Call Tools */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SectionHeader
                title={t('resources.assistants.admin.always_run_post_call_tools')}
                description={t('resources.assistants.admin.always_run_post_call_tools_description')}
              />
            </div>
            <div className="pt-1">
              <FormSwitch name="config.always_run_post_call_tools" control={control} />
            </div>
          </div>
        </div>
      </section>

      {/* Custom Configuration Section */}
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <SectionHeader
          title={t('resources.assistants.admin.custom_config')}
          description={t('resources.assistants.admin.custom_config_description')}
        />
        <CustomJsonEditor />
      </section>

      <WhatsAppRedirectDebug />
    </div>
  )
}
